<template>
  <div class="p-8 max-w-7xl mx-auto space-y-8 bg-[#f8fafc] min-h-full">
    
    <!-- En-tête de bienvenue du Dashboard -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm">
      <div>
        <h1 class="text-xl font-bold text-slate-900">Tableau de bord de la journée</h1>
        <p class="text-xs text-slate-400 mt-0.5">Suivi en temps réel des consultations et de l'état de la file d'attente.</p>
      </div>
      <div class="flex items-center gap-3">
        <div class="px-3.5 py-2 rounded-xl bg-blue-50 text-blue-600 text-xs font-semibold flex items-center gap-2 border border-blue-100/50">
          <svg class="w-4 h-4 animate-spin-slow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Heure actuelle : {{ currentTime }}</span>
        </div>
      </div>
    </div>

    <!-- Cartes Statistiques (Style Mediczen Cards) -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      
      <!-- Patients Aujourd'hui -->
      <div class="bg-white rounded-2xl p-6 border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between mb-4">
          <span class="text-xs font-bold text-slate-400 uppercase tracking-wider">Patients Aujourd'hui</span>
          <div class="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
            <i class="fas fa-user-md text-sm"></i>
          </div>
        </div>
        <div class="flex items-baseline justify-between">
          <h3 class="text-3xl font-extrabold text-slate-900">{{ todayAppointmentsCount }}</h3>
          <span class="text-[11px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg">Total prévus</span>
        </div>
      </div>

      <!-- Patients Restants -->
      <div class="bg-white rounded-2xl p-6 border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between mb-4">
          <span class="text-xs font-bold text-slate-400 uppercase tracking-wider">Patients Restants</span>
          <div class="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
            <i class="fas fa-procedures text-sm"></i>
          </div>
        </div>
        <div class="flex items-baseline justify-between">
          <h3 class="text-3xl font-extrabold text-slate-900">{{ remainingAppointmentsCount }}</h3>
          <span class="text-[11px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-lg">En attente</span>
        </div>
      </div>

      <!-- Patients Terminés -->
      <div class="bg-white rounded-2xl p-6 border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between mb-4">
          <span class="text-xs font-bold text-slate-400 uppercase tracking-wider">Patients Terminés</span>
          <div class="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
            <i class="fas fa-clock text-sm"></i>
          </div>
        </div>
        <div class="flex items-baseline justify-between">
          <h3 class="text-3xl font-extrabold text-slate-900">{{ completedAppointmentsCount }}</h3>
          <span class="text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-lg">Consultés</span>
        </div>
      </div>

    </div>

    <!-- Section Tableau des patients de la journée -->
    <div class="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 class="text-base font-bold text-slate-900">File d'attente active</h3>
          <p class="text-xs text-slate-400">Liste des patients programmés (triés par ordre décroissant)</p>
        </div>
        <span class="text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-xl">
          {{ sortedAppointments.length }} actif(s)
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-slate-100 text-left">
          <thead class="bg-slate-50/50">
            <tr>
              <th class="px-6 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Heure</th>
              <th class="px-6 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Nom</th>
              <th class="px-6 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Prénom</th>
              <th class="px-6 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Numéro</th>
              <th class="px-6 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Statut</th>
              <th class="px-6 py-3.5 text-right text-[11px] font-bold text-slate-400 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 text-xs">
            <tr v-if="sortedAppointments.length === 0">
              <td colspan="6" class="px-6 py-12 text-center text-slate-400 italic">
                Aucun patient en attente pour le moment.
              </td>
            </tr>
            <tr v-for="patient in sortedAppointments" :key="patient.id || patient._id" class="hover:bg-slate-50/60 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap font-mono font-semibold text-slate-700">{{ patient.timeSlot }}</td>
              <td class="px-6 py-4 whitespace-nowrap font-bold text-slate-900">{{ patient.lastName }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-slate-600">{{ patient.firstName }}</td>
              <td class="px-6 py-4 whitespace-nowrap font-mono text-slate-500">{{ patient.phoneNumber }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span @click="openStatusModal(patient)" 
                      :class="{
                        'bg-blue-50 text-blue-700 border-blue-200': patient.status === 'planifié',
                        'bg-amber-50 text-amber-700 border-amber-200': patient.status === 'appeler',
                        'bg-emerald-50 text-emerald-700 border-emerald-200': patient.status === 'en cours',
                        'bg-slate-100 text-slate-600 border-slate-200': patient.status === 'terminé'
                      }"
                      class="px-3 py-1 rounded-full text-[11px] font-bold border cursor-pointer inline-flex items-center gap-1.5 shadow-xs hover:opacity-80 transition-opacity"
                >
                  <span class="w-1.5 h-1.5 rounded-full" :class="{
                    'bg-blue-500': patient.status === 'planifié',
                    'bg-amber-500': patient.status === 'appeler',
                    'bg-emerald-500': patient.status === 'en cours',
                    'bg-slate-400': patient.status === 'terminé'
                  }"></span>
                  {{ patient.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right">
                <button v-if="patient.status !== 'terminé'" 
                        @click="markAsCompleted(patient._id || patient.id)" 
                        class="w-7 h-7 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors inline-flex items-center justify-center font-bold" 
                        title="Marquer comme terminé">
                  ✕
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal pour sélectionner un nouveau statut -->
    <div v-if="isEditingStatus" class="fixed inset-0 flex items-center justify-center z-50 bg-slate-950/30 backdrop-blur-xs p-4">
      <div class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl border border-slate-100">
        <h3 class="text-base font-bold text-slate-900 mb-1">Modifier le statut</h3>
        <p class="text-xs text-slate-400 mb-4">Mettez à jour l'étape du patient dans la file d'attente.</p>
        
        <select v-model="selectedStatus" class="w-full border border-slate-200 rounded-xl p-3 mb-6 bg-slate-50 text-slate-800 text-xs font-semibold focus:outline-none focus:border-blue-500">
          <option value="planifié">Planifié</option>
          <option value="appeler">Appeler</option>
          <option value="en cours">En cours</option>
          <option value="terminé">Terminé</option>
        </select>
        
        <div class="flex justify-end gap-2">
          <button @click="closeStatusModal" class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
            Annuler
          </button>
          <button @click="updateStatus(patientId)" class="px-4 py-2 rounded-xl text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-all">
            Sauvegarder
          </button>
        </div>
      </div>
    </div>

    <!-- Notification Toast -->
    <div v-if="notification" class="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-5 py-3.5 rounded-2xl shadow-xl flex flex-col gap-2 max-w-sm w-full border border-slate-800">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2.5">
          <div class="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold">
            ✓
          </div>
          <span class="text-xs font-medium">{{ notification }}</span>
        </div>
        <button @click="notification = null" class="text-slate-400 hover:text-white text-xs">✕</button>
      </div>
      <div class="relative w-full h-1 bg-slate-800 rounded-full overflow-hidden">
        <div class="absolute h-full bg-emerald-500 transition-all duration-100" :style="{ width: progressBarWidth + '%' }"></div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DashboardView',
  data() {
    return {
      todayAppointments: [],
      todayAppointmentsCount: 0,
      completedAppointmentsCount: 0,
      remainingAppointmentsCount: 0,
      notification: null,
      isEditingStatus: false,
      selectedStatus: '',
      patientId: null,
      progressBarWidth: 100,
      notificationDuration: 5000,
      notificationInterval: null,
      webSocket: null,
      pingInterval: null,
    };
  },

  computed: {
    // Tri décroissant basé sur l'heure (du plus tardif/récent au plus tôt)
    sortedAppointments() {
      return this.todayAppointments
        .filter(patient => patient.status !== 'terminé')
        .sort((a, b) => {
          const [aHour, aMinute] = (a.timeSlot || '00:00').split(':').map(Number);
          const [bHour, bMinute] = (b.timeSlot || '00:00').split(':').map(Number);

          if (aHour !== bHour) {
            return bHour - aHour; // Tri décroissant par heure
          }
          return bMinute - aMinute; // Tri décroissant par minute
        });
    },
    currentTime() {
      const now = new Date();
      return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
  },
  methods: {
    openStatusModal(patient) {
      this.selectedStatus = patient.status;
      this.patientId = patient._id || patient.id;
      this.isEditingStatus = true;
    },
    closeStatusModal() {
      this.isEditingStatus = false;
      this.selectedStatus = '';
    },
    showNotification(message) {
      this.notification = message;
      this.progressBarWidth = 100;

      if (this.notificationInterval) {
        clearInterval(this.notificationInterval);
      }

      this.notificationInterval = setInterval(() => {
        this.progressBarWidth -= 100 / (this.notificationDuration / 100);
        if (this.progressBarWidth <= 0) {
          this.clearNotification();
        }
      }, 100);

      setTimeout(() => {
        this.clearNotification();
      }, this.notificationDuration);
    },
    clearNotification() {
      clearInterval(this.notificationInterval);
      this.notification = null;
      this.progressBarWidth = 100;
    },
    setupWebSocket() {
      if (this.webSocket) {
        this.webSocket.close();
      }

      this.webSocket = new WebSocket('wss://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/ws');

      this.webSocket.onopen = () => {
        this.pingInterval = setInterval(() => {
          if (this.webSocket.readyState === WebSocket.OPEN) {
            this.webSocket.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      this.webSocket.onmessage = (event) => {
        const message = JSON.parse(event.data);
        if (message.type === 'appointmentUpdate') {
          this.fetchAppointments();
        }
      };

      this.webSocket.onclose = () => {
        clearInterval(this.pingInterval);
        setTimeout(() => {
          this.setupWebSocket();
        }, 5000);
      };

      this.webSocket.onerror = () => {
        this.webSocket.close();
      };
    },
    calculateRemainingAppointments() {
      const completedAppointments = this.todayAppointments.filter(
        appointment => appointment.status === 'terminé'
      );

      this.completedAppointmentsCount = completedAppointments.length;
      this.remainingAppointmentsCount = this.todayAppointmentsCount - completedAppointments.length;
    },
    async fetchAppointments() {
      try {
        const response = await axios.get('https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/appointments-today');
        this.todayAppointments = response.data;
        this.todayAppointmentsCount = this.todayAppointments.length;
        this.calculateRemainingAppointments();
      } catch (error) {
        console.error('Erreur lors de la récupération des rendez-vous:', error);
      }
    },
    async updateStatus() {
      try {
        await axios.put(`https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/appointments/${this.patientId}/status`, { status: this.selectedStatus });
        this.showNotification('Statut mis à jour avec succès.');
        this.isEditingStatus = false;
        this.fetchAppointments();
      } catch (error) {
        console.error('Erreur lors de la mise à jour du statut:', error);
        this.showNotification('Erreur lors de la mise à jour du statut.');
      }
    },
    async markAsCompleted(patientId) {
      try {
        this.patientId = patientId;
        this.selectedStatus = 'terminé';
        await this.updateStatus();
      } catch (error) {
        console.error('Erreur lors de la mise à jour directe :', error);
      }
    }
  },
  mounted() {
    this.fetchAppointments();
    this.setupWebSocket();
    setInterval(() => {
      this.$forceUpdate();
    }, 60000);
  },
};
</script>